import React, { useEffect, useState } from "react";
import { X, Share, PlusSquare } from "lucide-react";

const DISMISS_KEY = "solvana-install-dismissed";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS gives no beforeinstallprompt, show the manual card in Safari.
    let iosTimer;
    if (isIOS()) iosTimer = setTimeout(() => setShow(true), 1500);

    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };

  const install = async () => {
    if (isIOS() && !deferred) { setIosHelp(true); return; }
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); }
    setDeferred(null);
  };

  if (!show) return null;

  return (
    <div data-testid="install-prompt" style={{ position: "fixed", left: 16, right: 16, bottom: 16, zIndex: 200, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
      <div style={{ pointerEvents: "auto", width: "100%", maxWidth: 420, background: "#1a1712", color: "#FBF8F2", borderRadius: 14, padding: "16px 18px", boxShadow: "0 18px 50px -18px rgba(0,0,0,.6)", border: "1px solid rgba(199,154,75,.35)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
          <img src="/icon-192.png" alt="Solvana" width={44} height={44} style={{ borderRadius: 10, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--serif)", fontSize: 16, marginBottom: 3 }}>Install the Solvana app</div>
            <div style={{ fontSize: 12.5, color: "#c9b487", lineHeight: 1.5 }}>Add it to your home screen for one-tap access.</div>
          </div>
          <button onClick={dismiss} data-testid="install-dismiss" aria-label="Dismiss" style={{ background: "none", border: "none", color: "#8b8478", cursor: "pointer", padding: 2, flexShrink: 0 }}><X size={18} /></button>
        </div>

        {iosHelp || (isIOS() && !deferred) ? (
          <div style={{ marginTop: 12, fontSize: 12.5, color: "#e7ddc9", lineHeight: 1.7, borderTop: "1px solid rgba(199,154,75,.25)", paddingTop: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>1. Tap the Share icon <Share size={15} style={{ color: "#C79A4B" }} /></div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>2. Choose “Add to Home Screen” <PlusSquare size={15} style={{ color: "#C79A4B" }} /></div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button onClick={install} data-testid="install-accept" style={{ flex: 1, background: "#C79A4B", color: "#1a1712", border: "none", borderRadius: 9, padding: "10px 14px", fontSize: 13.5, fontWeight: 600, letterSpacing: ".02em", cursor: "pointer" }}>Install</button>
            <button onClick={dismiss} style={{ background: "transparent", color: "#c9b487", border: "1px solid rgba(199,154,75,.4)", borderRadius: 9, padding: "10px 14px", fontSize: 13.5, cursor: "pointer" }}>Not now</button>
          </div>
        )}
      </div>
    </div>
  );
}
